/* eslint-disable @typescript-eslint/no-explicit-any */
import { COMPOSITE, DOCUMENT } from 'src/pages/editor/components/layer/layerTypes';

import { RECTANGLE_WARP, PERSPECTIVE_WARP, SMOOTH_WARP, TEXTURE_WARP } from 'src/models/documentWarps';
import { PERSPECTIVE_TRANSFORM, TUBE_TRANSFORM, PLACEMENT_TRANSFORM } from 'src/models/transforms';
import { getSurfaces } from 'src/selectors/pages/editor';
import {
    ENGRAVING_CHANNEL,
    GLOW_FILTER,
    GLOW_TRANSFORM_ID,
    LETTERPRESS_CHANNEL,
    LETTERPRESS_FILTER,
    LETTERPRESS_TRANSFORM_ID,
    MASK,
    NORMAL,
} from 'src/config/constants';
import { getAllDocuments, getAllOverlays, getAllBackgrounds, getSceneSize } from 'src/selectors/layers';
import { getAllTransforms } from 'src/selectors/transforms';
import { buildTransformsArray } from './transforms';
import {
    buildAutomask,
    buildDocumentObject,
    buildEngravingImage,
    buildFinishComposite,
    buildMaskObject,
} from './documents';
import { buildOverlayObject } from './overlays';
import { buildBackgroundObject, sizeToString } from './backgrounds';
import { RootState } from 'src/store';
import { BackgroundType } from 'src/types/background';
import { SurfaceType } from 'src/types/surface';
import {
    AutoMaskInstructionType,
    CompositeTreeRootType,
    CompositeTreeType,
    DocumentXmlObject,
    EngravingCompositeType,
    MaskImageType,
    XMLCompositeType,
} from 'src/types/xml';
import { PlacementTransformType, TubeTransformType } from 'src/types/transforms';
import { Document, DocumentSliceState } from 'src/pages/editor/components/documents/slice';
import { OverlaySliceState } from 'src/pages/editor/components/overlays/slice';
import { getActiveEffectType, getEffectProperties } from 'src/util/effects';
import { EffectType } from 'src/config/effects';

function handleEngraving(
    id: string,
    document: DocumentXmlObject,
    root: XMLCompositeType,
    finishes: XMLCompositeType[],
    mask?: MaskImageType,
    isEngraving?: boolean,
    engravingColor?: string,
) {
    const composite = Object.assign({}, root);
    const documentObject = Object.assign({}, document);
    let engraving;
    let compositeEngraving: EngravingCompositeType;

    if (isEngraving) {
        engraving = buildEngravingImage(root.size, engravingColor);
    }

    if (mask && engraving) {
        compositeEngraving = {
            size: root.size,
            name: `${id}-engraving-wrapper`,
            type: COMPOSITE,
            mode: documentObject.mode,
            depth: 1,
        };
        const engraveDoc = Object.assign({}, documentObject);
        engraveDoc.type = DOCUMENT;
        engraveDoc.mode = MASK;
        engraveDoc.channel = ENGRAVING_CHANNEL;
        engraveDoc.depth = 0;
        compositeEngraving.children = [engraveDoc, engraving];
        composite.children = [compositeEngraving, ...finishes, mask];
    } else if (engraving) {
        documentObject.type = DOCUMENT;
        documentObject.mode = MASK;
        documentObject.channel = ENGRAVING_CHANNEL;
        composite.mode = NORMAL;
        composite.children = [documentObject, ...finishes, engraving];
    } else if (mask) {
        composite.children = [documentObject, ...finishes, mask];
    } else {
        composite.children = [documentObject, ...finishes];
    }

    return composite;
}

function getLetterPressTransformId(documentId: string): string {
    return `${documentId}-${LETTERPRESS_TRANSFORM_ID}`;
}

function getGlowTransformId(documentId: string): string {
    return `${documentId}-${GLOW_TRANSFORM_ID}`;
}

function buildLetterPressDocument(doc: Document, size: string, mode: string): DocumentXmlObject {
    const transformId = getLetterPressTransformId(doc.id);
    const letterPressDoc: DocumentXmlObject = {
        size,
        name: `${doc.id} Letterpress Transform`,
        type: DOCUMENT,
        mode,
        depth: 0,
        xform: transformId,
        channel: LETTERPRESS_CHANNEL,
        page: doc.page,
        offset: 0,
    };

    return letterPressDoc;
}

function documentCompositeBuilder(doc: Document, size: string, hideEnabled: boolean, surfaces: SurfaceType[]) {
    const { warpType: documentWarp, hidden, maskHidden, id, automask, finishes } = doc;
    const surface = surfaces.find((surf) => surf.index === doc.page);
    if (hideEnabled && hidden) {
        return null;
    }

    // Get effect using utility functions
    const effectType = getActiveEffectType(doc);
    const effect = getEffectProperties(doc);

    const documentObject: DocumentXmlObject = buildDocumentObject(doc);
    documentObject.depth = 0;

    let mask: MaskImageType | undefined = undefined;
    if (!hideEnabled || !maskHidden) {
        mask = buildMaskObject(doc, size);
    }

    // document should always sit behind mask and finsihes so increase depth accordingly.
    if (mask) documentObject.depth++;
    if (finishes && finishes.length > 0) documentObject.depth++;

    const transformsData = doc.transforms || [];
    let composite: XMLCompositeType = {
        size,
        name: `${id}-wrapper`,
        type: COMPOSITE,
        mode: effectType === EffectType.ENGRAVING ? NORMAL : documentObject.mode,
        children: [] as Record<string, any>[],
    };

    documentObject.mode = NORMAL;

    if (
        documentWarp === RECTANGLE_WARP ||
        documentWarp === PERSPECTIVE_WARP ||
        documentWarp === SMOOTH_WARP ||
        documentWarp === TEXTURE_WARP
    ) {
        // build finishe composite if finishes exist
        const finishComposites =
            finishes?.map((finish) => buildFinishComposite({ finish, doc: documentObject, depth: mask ? 1 : 0 })) ?? [];

        documentObject.xform = transformsData[0].id;

        if (automask) {
            composite.children = [documentObject];
            const automaskInstruction: AutoMaskInstructionType = buildAutomask(documentObject, surface);
            automaskInstruction.xform = transformsData[0].id;
            composite.children.push(...finishComposites);
            composite.children.push(automaskInstruction);
        } else if (effectType === EffectType.LETTERPRESS && effect?.type === EffectType.LETTERPRESS) {
            // Letter press: create dual-document structure with wrapper composite
            const letterPressDoc = buildLetterPressDocument(doc, size, effect.mode);
            letterPressDoc.size = documentObject.size; // letterpress document should be same size as main document

            // Create wrapper composite for letterPress document with same transform and parent size
            const letterPressWrapper: XMLCompositeType = {
                size: documentObject.size,
                name: `${id}-Letterpress-wrapper`,
                type: COMPOSITE,
                mode: 'multiply',
                depth: 0,
                xform: transformsData[0].id, // same transform as original document
                children: [letterPressDoc],
            };

            // Main document gets positioned with warp transform
            documentObject.xform = transformsData[0].id;
            documentObject.depth = 1; // comes after letterPress wrapper

            if (mask) {
                composite.children = [letterPressWrapper, documentObject, mask];
            } else {
                composite.children = [letterPressWrapper, documentObject];
            }
        } else if (effectType === EffectType.GLOW) {
            // Glow: apply GlowFilter transform to document
            const glowTransformId = getGlowTransformId(id);

            // Apply glow transform to document
            documentObject.xform = glowTransformId;

            // Add Page element for glow documents
            documentObject.addPageElement = true;

            // Create wrapper composite for letterPress document with same transform and parent size
            const glowComposite: XMLCompositeType = {
                size: documentObject.size,
                name: `${id}-Glow-wrapper`,
                type: COMPOSITE,
                mode: 'normal',
                depth: 0,
                xform: transformsData[0].id, // same transform as original document
                children: [documentObject],
            };

            if (mask) {
                composite.children = [glowComposite, mask];
            } else {
                composite.children = [glowComposite];
            }
        } else {
            composite = handleEngraving(
                id,
                documentObject,
                composite,
                finishComposites,
                mask,
                effectType === EffectType.ENGRAVING,
                effect?.type === EffectType.ENGRAVING ? effect.color : undefined,
            );
        }
    } else {
        // CYLINDER_WARP
        const compositeXform = transformsData.find((transform) => transform.type === PERSPECTIVE_TRANSFORM)!;
        const tubeTransform = transformsData.find(
            (transform) => transform.type === TUBE_TRANSFORM,
        ) as TubeTransformType;
        const placementTransform = transformsData.find(
            (transform) => transform.type === PLACEMENT_TRANSFORM,
        ) as PlacementTransformType;

        let compositeChildren;
        let engraving;
        const tubeTransformSize = tubeTransform
            ? `${tubeTransform.width},${tubeTransform.height}`
            : documentObject.size;
        const placementTransformSize = placementTransform
            ? `${placementTransform.width},${placementTransform.height}`
            : documentObject.size;

        const tempChildren: any[] = [documentObject];
        if (effectType === EffectType.ENGRAVING) {
            documentObject.channel = ENGRAVING_CHANNEL;
            documentObject.depth = 0;
            documentObject.mode = MASK;
            documentObject.xform = placementTransform ? placementTransform.id : tubeTransform.id;
            engraving = buildEngravingImage(
                placementTransform ? placementTransformSize : tubeTransformSize,
                effect?.type === EffectType.ENGRAVING ? effect.color : undefined,
            );
            engraving.xform = placementTransform ? placementTransform.id : tubeTransform.id;
            tempChildren.push(engraving);
        } else if (automask && surface) {
            const automaskInstruction = buildAutomask(documentObject, surface);
            automaskInstruction.xform = placementTransform ? placementTransform.id : tubeTransform.id;
            tempChildren.push(automaskInstruction);
        }

        if (placementTransform) {
            documentObject.xform = placementTransform.id;
            compositeChildren = [
                {
                    size: placementTransformSize,
                    name: `${id}-inner-placement-wrapper`,
                    mode: NORMAL,
                    type: COMPOSITE,
                    xform: tubeTransform.id,
                    depth: mask ? 1 : 0,
                    children: tempChildren,
                },
            ];
        } else {
            documentObject.xform = tubeTransform.id;
            compositeChildren = tempChildren;
        }

        composite.children = [
            {
                size: tubeTransformSize,
                name: `${id}-inner-wrapper`,
                mode: NORMAL,
                type: COMPOSITE,
                xform: compositeXform.id,
                depth: mask ? 1 : 0,
                children: compositeChildren,
            },
        ];

        // add finishes for cylinder warp if they exist
        const finishComposites =
            finishes?.map((finish) =>
                buildFinishComposite({
                    finish,
                    doc: documentObject,
                    depth: mask ? 1 : 0,
                    size: tubeTransformSize,
                }),
            ) ?? [];
        composite.children.push(...finishComposites);

        if (mask) {
            composite.children.push(mask);
        }
    }

    return composite;
}

function buildComposite(
    maxSize: string,
    backgrounds: BackgroundType[],
    documentsData: DocumentSliceState,
    overlaysData: OverlaySliceState,
    hideEnabled: boolean,
    surfaces: SurfaceType[],
) {
    const backgroundsItems = backgrounds.map((bkg) => buildBackgroundObject(bkg));
    const documents = documentsData
        .map((doc) => documentCompositeBuilder(doc, maxSize, hideEnabled, surfaces))
        .filter(Boolean);
    const overlays = overlaysData.map((overlay) => buildOverlayObject(overlay, maxSize, hideEnabled)).filter(Boolean);

    const children = [...overlays, ...documents, ...backgroundsItems].map((node, index) => {
        // TODO: Find a better option to allow adding depth without spamming types
        const child = node as Record<string, any>;
        if (child) {
            child.depth = index; // eslint-disable-line no-param-reassign
        }
        return child;
    });

    return {
        children,
        size: maxSize,
        mode: NORMAL,
        depth: 0,
        name: 'root',
    } as CompositeTreeRootType;
}

export function buildCompositeTree(state: RootState, background: BackgroundType | undefined, hideEnabled: boolean) {
    // eslint-disable-line import/prefer-default-export
    let backgrounds = getAllBackgrounds(state);
    const surfaces = getSurfaces(state);
    let maxSize = null;

    if (!background && backgrounds.length === 0) {
        return undefined;
    }

    if (background) {
        backgrounds = [background];
        maxSize = sizeToString(background);
    } else {
        const firstDocumentSize = getSceneSize(state);
        maxSize = sizeToString(firstDocumentSize);
    }

    const documents = getAllDocuments(state);
    const overlays = getAllOverlays(state);
    const transforms = getAllTransforms(state) || [];

    // Add LetterpressFilter transforms for documents with letterPress enabled
    const letterPressTransforms = documents
        .filter((doc) => !doc.hidden && getActiveEffectType(doc) === EffectType.LETTERPRESS)
        .map((doc) => {
            const effect = getEffectProperties(doc);
            return {
                type: LETTERPRESS_FILTER,
                id: getLetterPressTransformId(doc.id),
                depth: effect?.type === EffectType.LETTERPRESS ? effect.depth : 2,
            };
        });

    // Add GlowFilter transforms for documents with glow enabled
    const glowTransforms = documents
        .filter((doc) => !doc.hidden && getActiveEffectType(doc) === EffectType.GLOW)
        .map((doc) => {
            const effect = getEffectProperties(doc);
            return {
                type: GLOW_FILTER,
                id: getGlowTransformId(doc.id),
                r: effect?.type === EffectType.GLOW ? effect.r : 0,
                g: effect?.type === EffectType.GLOW ? effect.g : 0,
                b: effect?.type === EffectType.GLOW ? effect.b : 0,
                radius: effect?.type === EffectType.GLOW ? effect.radius : 10,
                light: effect?.type === EffectType.GLOW ? effect.light : false,
            };
        });

    const allTransforms = [...transforms, ...letterPressTransforms, ...glowTransforms] as any[];

    const data: CompositeTreeType = {
        root: buildComposite(maxSize, backgrounds, documents, overlays, hideEnabled, surfaces),
        transforms: buildTransformsArray(allTransforms, maxSize),
    };

    return data;
}
